<script setup>

import { computed, ref, watch } from 'vue';
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import { Inertia } from "@inertiajs/inertia";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import SampleIndexFilters from "@/Components/Admin/Sample/SampleIndexFilters.vue";
import html2pdf from 'html2pdf.js';

const props = defineProps({
  samples: Array,
  garments: Array,
  filters: Object,
  sorting: Object,
  departments: Array,
  fabrics: Array,
  clients: Array,
  sizeScales: Array,
  colors: Array,
  locations: Array,
});

console.log('Props:', props);

const images = computed(() => {
  return props.samples.data.flatMap(sample =>
    sample.image_media.map(image => ({
      style_number: sample.style_number,
      filename: image.filename,
      url: image.url
    }))
  );
});

const closingSample = ref(null);
const deletingSample = ref(null);

const fallbackToOldImage = (event) => {
  const current = event.target.src;
  if (current.includes('/sample/')) {
    event.target.src = current.replace('/sample/', '/sample_database/');
  }
};

const closeSample = (sample) => {
  Inertia.post(route('admin.samples.close', { sample: sample.sample_id }), {
    preserveScroll: true,
    onError: (error) => alert(`There was an error. Try again later`)
  });
  closingSample.value = null;
};

const openCloseSampleModal = (sample) => {
  closingSample.value = sample;
}

const closeCloseSampleModal = () => {
  closingSample.value = null;
}

const deleteSample = (sample) => {
  Inertia.delete(route('admin.samples.destroy', { sample: sample.sample_id }), {
    preserveScroll: true,
    onError: (error) => alert(`There was an error. Try again later`)
  });
  deletingSample.value = null;
};

const openDeleteSampleModal = (sample) => {
  deletingSample.value = sample;
}

const closeDeleteSampleModal = () => {
  deletingSample.value = null;
}

const showGallery = ref(false);

const openGalleryModal = () => {
  showGallery.value = true;
}

const closeGalleryModal = () => {
  showGallery.value = false;
}

watch(sorting, (newSorting) => {
  Inertia.get(route('admin.samples.index'), {
    filters: props.filters,
    sorting: newSorting,
  }, {
    preserveScroll: true,
    preserveState: true,
    replace: true,
  });
});

const defaultSorting = { column: 'modifieddate', is_ascending: false };

const loadPersisted = () => {
  try {
    const f = localStorage.getItem('samples.filters');
    const s = localStorage.getItem('samples.sorting');
    return {
      filters: f ? JSON.parse(f) : null,
      sorting: s ? JSON.parse(s) : null,
    };
  } catch (e) { return { filters: null, sorting: null }; }
}

const persisted = loadPersisted();
const sorting = ref(Object.assign({}, defaultSorting, persisted.sorting || props.sorting));

const sortTable = (column) => {
  if (column === sorting.value.column) {
    sorting.value.is_ascending = !sorting.value.is_ascending;
  } else {
    sorting.value = { column, is_ascending: true };
  }
}

const sortingClass = (column) => {
  if (column !== sorting.value.column) {
    return ['fa', 'fa-sort'];
  }

  return sorting.value.is_ascending ? ['fa', 'fa-sort-down'] : ['fa', 'fa-sort-up'];
}

const printGallery = () => {
  const gallery = document.getElementById('gallery-print');
  const iframe = document.getElementById('print-iframe');

  const doc = iframe.contentWindow.document;
  doc.open();

  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map(style => style.outerHTML)
    .join('');

  doc.write(`
    <html>
      <head>
        <title>Print Gallery</title>
        ${styles}
        <style>
          @media print {
            body {
              margin: 0;
              padding: 1rem;
              font-family: inherit;
              background: white;
            }
            .print-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
              gap: 1rem;
            }
            .print-item {
              page-break-inside: avoid;
              break-inside: avoid;
              text-align: center;
              border: 1px solid #ddd;
              padding: 0.5rem;
              border-radius: 0.5rem;
            }
            img {
              max-height: 300px;
              object-fit: contain;
              display: block;
              margin: auto;
            }
            p {
              margin-top: 0.5rem;
              font-weight: bold;
              text-align: center;
            }
          }
        </style>
      </head>
      <body>
        ${gallery.innerHTML}
      </body>
    </html>
  `);

  doc.close();

  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };
}

const exportGallery = () => {
  const element = document.getElementById('gallery-print');

  html2pdf()
    .set({
      margin: 0.5,
      filename: 'gallery-export.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true
      },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    })
    .from(element)
    .save();
};

</script>

<template>
  <AppLayout title="Database Sample List">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Database Samples
      </h2>
    </template>

    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div>
          <a :href="route('admin.samples.create')" class="mr-2">
            <PrimaryButton>Add Sample Database</PrimaryButton>
          </a>
          <SecondaryButton @click="openGalleryModal">View Gallery</SecondaryButton>
        </div>
        <div class="my-5 grid gap-2 lg:grid-cols-4 md:grid-cols-2 grid-cols-1">
          <SampleIndexFilters :garments="garments" :departments="departments" :fabrics="fabrics" :sizeScale="sizeScales" :clients="clients" :filters="filters" :colors="colors" :locations="locations" :sorting="sorting"></SampleIndexFilters>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full w-full text-left text-sm font-light">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2">Thumbnail</th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('sample_id_val')">
                  Sample Name <span :class="sortingClass('sample_id_val')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('department')">
                  Department <span :class="sortingClass('department')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('garment')">
                  Garment <span :class="sortingClass('garment')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer">
                  Garment Description
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('fabric')">
                  Material <span :class="sortingClass('fabric')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer">
                  Client</th>
                <th scope="col" class="px-4 py-2 cursor-pointer">Location</th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
                v-for="sample in samples.data">
                <td class="px-4 py-2 break-words">
                  <img v-if="sample.image_media[0]?.url" :src="sample.image_media[0]?.url"
                    :alt="sample.image_media[0]?.filename" class="w-16 h-16 object-cover"
                    @error="event => fallbackToOldImage(event)">
                </td>
                <td class="px-4 py-2 break-words" v-text="sample.sample_id_val"></td>
                <td class="px-4 py-2 break-words" v-text="sample.department"></td>
                <td class="px-4 py-2 break-words" v-text="sample.garment?.garmentName"></td>
                <td class="px-4 py-2 break-words" v-text="sample.detail_description"></td>
                <td class="px-4 py-2 break-words" v-text="sample.fabric"></td>
                <td class="px-4 py-2 break-words" v-text="sample.client?.client"></td>
                <td class="px-4 py-2 break-words" v-text="sample.location?.name"></td>
                <td>
                  <a :href="route('admin.samples.edit', { sample: sample })" class="fa fa-pen mr-1 p-1"
                    :title="`Edit ${sample.sample_id}`">
                  </a>
                  <button class="fa fa-times p-1" @click="openCloseSampleModal(sample)" :disabled="closingSample"
                    :title="`Close ${sample.sample_id_val}`"></button>
                  <button class="fa fa-trash p-1" @click="openDeleteSampleModal(sample)" :disabled="deletingSample"
                    :title="`Delete ${sample.sample_id_val}`"></button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="text-center mt-5">
            <PaginationLinks :pagination="samples" />
          </div>
        </div>
      </div>
    </div>
    <DialogModal :show="closingSample !== null" @close="closeCloseSampleModal">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        You are about to delete sample <b v-text="closingSample.sample_id_val"></b>.
        <br>Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeCloseSampleModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="closeSample(closingSample)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>

    <DialogModal :show="deletingSample !== null" @close="closeDeleteSampleModal">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        You are about to delete <b v-text="deletingSample.sample_id_value"></b>.
        <br>Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeDeleteSampleModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="deleteSample(deletingSample)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>

    <DialogModal :show="showGallery" @close="closeGalleryModal" max-width="7xl">
      <template #title>
        <b>Image Gallery</b>
      </template>

      <template #content>
        <div class="mb-4 text-left">
          <PrimaryButton @click="printGallery">
            Print
          </PrimaryButton>
          <PrimaryButton @click="exportGallery" class="ml-2">
            Export PDF
          </PrimaryButton>
        </div>

        <div id="gallery-print">
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <div v-for="(image, index) in images" :key="index"
              class="rounded-lg overflow-hidden shadow-lg flex flex-col items-center">
              <img :src="image.url" :alt="image.filename" class="max-w-full max-h-60 object-contain"
                @error="event => fallbackToOldImage(event)">
              <p class="text-center mt-2 font-semibold">Style number: <b>{{ image.style_number ? image.style_number :
                  'No data' }}</b></p>
            </div>
          </div>
        </div>
      </template>
    </DialogModal>

    <iframe id="print-iframe" style="display: none;"></iframe>

  </AppLayout>
</template>
